import { FeatureId } from '@weavix/models/src/features/feature';
import { ApiCategory, CatalogFeature, CatalogFeatureId, CatalogGateKind } from '@weavix/models/src/packaging/feature-catalog';
import { ProductPackage } from '@weavix/models/src/packaging/product-package';
import { PermissionAction } from '@weavix/models/src/permission/permissions.model';

/** Id of the package a site resolves to when neither the site nor its account names any. */
export const CORE_PRODUCT_PACKAGE_ID = 'core';

/** What a set of packages licenses: catalog features and the flags, actions and API categories behind them. */
export interface ProductPackageEntitlements {
    features: Set<CatalogFeatureId>;
    actions: Set<PermissionAction>;
    flags: Set<FeatureId>;
    apiCategories: Set<ApiCategory>;
}

/** True when both license exactly the same features, actions, flags and API categories (or both are null). */
export function sameEntitlements(a: ProductPackageEntitlements | null | undefined, b: ProductPackageEntitlements | null | undefined): boolean {
    if (!a || !b) return !a && !b;
    const same = <T>(x: ReadonlySet<T>, y: ReadonlySet<T>) => x.size === y.size && [...x].every(value => y.has(value));
    return same(a.features, b.features) && same(a.actions, b.actions) && same(a.flags, b.flags) && same(a.apiCategories, b.apiCategories);
}

/** Every flag and action some catalog feature gates; anything outside these sets is not licensed by packages. */
export interface CatalogGates {
    actions: Set<PermissionAction>;
    flags: Set<FeatureId>;
}

/**
 * Returns the packages a site holds: its own, otherwise the account default, otherwise Core.
 * An empty list counts as none, so it never strips a site below Core.
 */
export function resolveSiteProductPackages(siteProductPackages?: ReadonlyArray<string>, accountProductPackages?: ReadonlyArray<string>): string[] {
    if (siteProductPackages?.length) return [...siteProductPackages];
    if (accountProductPackages?.length) return [...accountProductPackages];
    return [CORE_PRODUCT_PACKAGE_ID];
}

/**
 * Unions what the named packages license. Unknown package ids and unknown features are skipped with a warning,
 * so a deleted package or an older client can never grant anything or crash.
 */
export function entitlementsFor(
    packageIds: ReadonlyArray<string>,
    definitions: ReadonlyArray<ProductPackage>,
    catalog: Readonly<Record<string, CatalogFeature>>,
): ProductPackageEntitlements {
    const byId = new Map(definitions.map(definition => [definition.id, definition]));
    const entitlements: ProductPackageEntitlements = { features: new Set(), actions: new Set(), flags: new Set(), apiCategories: new Set() };

    for (const packageId of new Set(packageIds)) {
        const definition = byId.get(packageId);
        if (!definition) {
            console.warn(`Package "${packageId}" is unknown and grants nothing. Is it deployed and syncing?`);
            continue;
        }

        for (const featureId of definition.features) {
            const feature = catalogFeature(catalog, featureId);
            if (!feature) {
                console.warn(`Package "${packageId}" lists unknown feature "${featureId}", which grants nothing.`);
                continue;
            }
            entitlements.features.add(featureId);
            addGate(entitlements, feature.gate);
        }
    }

    return entitlements;
}

/** An account's package assignments and the definitions it can see: enough to resolve any of its sites. */
export interface AccountProductPackageState {
    accountId: string;
    accountPackages?: ReadonlyArray<string>;
    sitePackages: Readonly<Record<string, ReadonlyArray<string> | undefined>>;
    definitions: ReadonlyArray<ProductPackage>;
}

/** The package ids a site resolved to, and what they license; `entitlements` is null when none of the ids is defined. */
export interface SiteEntitlementResolution {
    packageIds: string[];
    entitlements: ProductPackageEntitlements | null;
}

/**
 * Resolves a site (site, else account default, else Core), or every package held in the account when `facilityId` is null.
 * Another account's custom package grants nothing but still counts as defined; null entitlements mean the caller falls back.
 */
export function resolveSiteEntitlements(
    state: AccountProductPackageState,
    facilityId: string | null,
    catalog: Readonly<Record<string, CatalogFeature>>,
): SiteEntitlementResolution {
    const packageIds = facilityId
        ? resolveSiteProductPackages(state.sitePackages[facilityId], state.accountPackages)
        : [...resolveSiteProductPackages(undefined, state.accountPackages), ...Object.values(state.sitePackages).flatMap(ids => ids ?? [])];
    const defined = new Set(state.definitions.map(definition => definition.id));
    if (!packageIds.some(packageId => defined.has(packageId))) return { packageIds, entitlements: null };

    const usable = state.definitions.filter(definition => !definition.ownerAccountId || definition.ownerAccountId === state.accountId);
    return { packageIds, entitlements: entitlementsFor(packageIds, usable, catalog) };
}

/** Looks up a feature among the catalog's own keys only, so ids like `constructor` count as unknown. */
function catalogFeature(catalog: Readonly<Record<string, CatalogFeature>>, featureId: string): CatalogFeature | undefined {
    return Object.getOwnPropertyDescriptor(catalog, featureId)?.value as CatalogFeature | undefined;
}

/** Collects every flag and action the catalog gates, so callers can let everything else through unchanged. */
export function catalogGates(catalog: Readonly<Record<string, CatalogFeature>>): CatalogGates {
    const gates: CatalogGates = { actions: new Set(), flags: new Set() };
    for (const { gate } of Object.values(catalog)) {
        if (gate.kind === CatalogGateKind.Flag || gate.kind === CatalogGateKind.Actions) {
            if (gate.flag) gates.flags.add(gate.flag);
        }
        if (gate.kind === CatalogGateKind.Actions) gate.actions.forEach(action => gates.actions.add(action));
    }
    return gates;
}

/** True when packages license the action, or when no catalog feature gates it, in which case today's grants decide. */
export function isActionLicensed(action: PermissionAction, entitlements: ProductPackageEntitlements, gates: CatalogGates): boolean {
    return !gates.actions.has(action) || entitlements.actions.has(action);
}

/** True when packages license the flag, or when no catalog feature gates it, in which case the flag alone decides. */
export function isFlagLicensed(flag: FeatureId, entitlements: ProductPackageEntitlements, gates: CatalogGates): boolean {
    return !gates.flags.has(flag) || entitlements.flags.has(flag);
}

/**
 * Lists every way a set of package definitions is invalid: duplicate or blank ids, unknown features,
 * missing `requires` targets and `requires` cycles. An empty result means the set is valid.
 */
export function validateProductPackageDefinitions(
    definitions: ReadonlyArray<ProductPackage>,
    catalog: Readonly<Record<string, CatalogFeature>>,
): string[] {
    const problems: string[] = [];
    const byId = new Map<string, ProductPackage>();

    for (const definition of definitions) {
        if (!definition.id?.trim()) problems.push('a package has no id');
        else if (byId.has(definition.id)) problems.push(`${definition.id}: id is used by more than one package`);
        else byId.set(definition.id, definition);
    }

    for (const definition of byId.values()) {
        for (const featureId of definition.features ?? []) {
            if (!catalogFeature(catalog, featureId)) problems.push(`${definition.id}: feature "${featureId}" does not exist`);
        }
        for (const required of definition.requires ?? []) {
            if (!byId.has(required)) problems.push(`${definition.id}: requires "${required}", which does not exist`);
        }
    }

    for (const cycle of findRequiresCycles(byId)) problems.push(`requires cycle: ${cycle.join(' -> ')}`);

    return problems;
}

/** Returns each `requires` cycle once, as the path that closes it, e.g. `['a', 'b', 'a']`. */
function findRequiresCycles(byId: ReadonlyMap<string, ProductPackage>): string[][] {
    const cycles: string[][] = [];
    const done = new Set<string>();

    const visit = (id: string, path: string[]): void => {
        const start = path.indexOf(id);
        if (start >= 0) {
            cycles.push([...path.slice(start), id]);
            return;
        }
        if (done.has(id) || !byId.has(id)) return;

        for (const required of byId.get(id)?.requires ?? []) visit(required, [...path, id]);
        done.add(id);
    };

    for (const id of byId.keys()) visit(id, []);
    return cycles;
}

/** Adds the flag, actions or API category behind one catalog feature to the entitlements. */
function addGate(entitlements: ProductPackageEntitlements, gate: CatalogFeature['gate']): void {
    switch (gate.kind) {
        case CatalogGateKind.Flag:
            entitlements.flags.add(gate.flag);
            break;
        case CatalogGateKind.Actions:
            gate.actions.forEach(action => entitlements.actions.add(action));
            if (gate.flag) entitlements.flags.add(gate.flag);
            break;
        case CatalogGateKind.Api:
            entitlements.apiCategories.add(gate.category);
            break;
        case CatalogGateKind.NotGateable:
            break;
    }
}
